/**
 * Connected admin debug with data fetching and mutations.
 *
 * Self-contained - no props needed.
 */

import { useCallback, useEffect, useMemo, useState } from "react";

import { createDebugApi } from "@animus/frontend-features/admin/debug";
import {
  useApiConfig,
  useAuthStrategy,
} from "@animus/frontend-features/config";

import type { DebugAction, DebugStatus, HealthStatus } from "./admin-debug.tsx";
import { AdminDebug } from "./admin-debug.tsx";

function AdminDebugConnected() {
  const { baseUrl } = useApiConfig();
  const auth = useAuthStrategy();
  const api = useMemo(() => createDebugApi(baseUrl, auth), [baseUrl, auth]);

  const [status, setStatus] = useState<DebugStatus | null>(null);
  const [isLoadingStatus, setIsLoadingStatus] = useState(true);

  const [health, setHealth] = useState<HealthStatus | null>(null);
  const [isLoadingHealth, setIsLoadingHealth] = useState(true);
  const [healthError, setHealthError] = useState<string | null>(null);

  const [lastErrorTriggered, setLastErrorTriggered] = useState<Date | null>(
    null
  );
  const [lastSlowTriggered, setLastSlowTriggered] = useState<Date | null>(null);
  const [lastLogTriggered, setLastLogTriggered] = useState<Date | null>(null);

  const [isErrorPending, setIsErrorPending] = useState(false);
  const [isSlowPending, setIsSlowPending] = useState(false);
  const [isLogPending, setIsLogPending] = useState(false);

  const fetchStatus = useCallback(async () => {
    setIsLoadingStatus(true);
    const result = await api.getDebugStatus();

    if (result.isOk) {
      setStatus({
        status: result.value.status,
        user: result.value.user,
        timestamp: result.value.timestamp,
      });
    }
    setIsLoadingStatus(false);
  }, [api]);

  const fetchHealth = useCallback(async () => {
    setIsLoadingHealth(true);
    setHealthError(null);
    try {
      const res = await fetch(`${baseUrl}/health/verbose`);
      const data = await res.json();
      setHealth(data);
    } catch (err) {
      setHealthError(err instanceof Error ? err.message : "Unknown error");
    }
    setIsLoadingHealth(false);
  }, [baseUrl]);

  useEffect(() => {
    fetchStatus().catch(() => {
      // Error already handled in fetchStatus
    });
    fetchHealth().catch(() => {
      // Error already handled in fetchHealth
    });
  }, [fetchStatus, fetchHealth]);

  const actions: DebugAction[] = [
    {
      key: "error",
      title: "Trigger Test Error",
      description:
        "Throws an intentional error to test Sentry error tracking and OTEL trace error attributes.",
      buttonLabel: "Trigger Error",
      pendingLabel: "Triggering...",
      variant: "danger",
      isPending: isErrorPending,
      lastTriggered: lastErrorTriggered,
      onTrigger: async () => {
        setIsErrorPending(true);
        try {
          await api.triggerTestError();
          setLastErrorTriggered(new Date());
        } catch {
          // Error is expected - the endpoint throws intentionally
          setLastErrorTriggered(new Date());
        }
        setIsErrorPending(false);
      },
    },
    {
      key: "slow",
      title: "Trigger Slow Response",
      description:
        "Calls an endpoint that delays 150ms to test OTEL trace duration tracking.",
      buttonLabel: "Trigger Slow",
      pendingLabel: "Waiting...",
      variant: "warning",
      isPending: isSlowPending,
      lastTriggered: lastSlowTriggered,
      onTrigger: async () => {
        setIsSlowPending(true);
        await api.triggerSlowResponse();
        setLastSlowTriggered(new Date());
        setIsSlowPending(false);
      },
    },
    {
      key: "log",
      title: "Fire Test Logs",
      description:
        "Fires debug/info/warn logs to verify Loki and logging setup.",
      buttonLabel: "Fire Logs",
      pendingLabel: "Firing...",
      variant: "primary",
      isPending: isLogPending,
      lastTriggered: lastLogTriggered,
      onTrigger: async () => {
        setIsLogPending(true);
        await api.triggerTestLog();
        setLastLogTriggered(new Date());
        setIsLogPending(false);
      },
    },
  ];

  return (
    <AdminDebug
      actions={actions}
      health={health}
      healthError={healthError}
      isLoadingHealth={isLoadingHealth}
      isLoadingStatus={isLoadingStatus}
      status={status}
    />
  );
}

export { AdminDebugConnected };
